// Shared helpers for rendering SPR message-log entries (used by MessageLogPage and the
// reconciliation list). Extracted from TaskConfigurationPage so both screens read one implementation.

// Colour class for one line of an oracle-ohip "story" (one HTTP call per line).
export const classifyStoryLine = (line) => {
    // A reason the record failed with no HTTP call to blame — "✗ Missing linked Profile 13687183
    // (CompanyContact)". oracle-ohip appends it to the story as its own line, because a run that never
    // reached a create call otherwise showed a red step whose whole story was a successful GET. Matched
    // at the START of the line: the "✗ REJECTED"/"✗ <o:errorCode>" flags sit mid-line after "→ <status>",
    // and those keep their own classification below.
    if (/^\s*✗/.test(line)) return 'error';
    if (/→\s*SKIPPED\b/i.test(line)) return 'skipped';               // deliberately not fired (grey, struck through)
    if (/→\s*[45]\d\d\b/.test(line)) return 'error';                  // 4xx/5xx = a real failure (red)
    if (/✗\s*REJECTED|⚠|not processed/i.test(line)) return 'warning'; // warning on a 2xx (yellow, not error)
    if (/→\s*[123]\d\d\b/.test(line)) return 'success';               // 1xx/2xx/3xx, clean (green)
    return 'neutral';
};

// The PREFIXED ANNOTATIONS a record carries in status_history — "[cc] all token(s) swapped" and any
// other stamp written by appendSprHistory with a replacePrefix.
//
// THEY ARE NOT IN `comment`, AND CANNOT BE. oracle-ohip's terminal stamp REPLACES the record comment
// wholesale at the end of every run, so a note handed to updateSyncRecord earlier in the run is
// discarded — which is exactly why the swap result was visible in Status History and missing from the
// Comment column, the one place anybody reads. appendSprHistory keeps the LATEST entry per prefix, so
// taking the last match per prefix here yields one current line each, never a pile of reprocesses.
export const annotationLines = (statusHistory) => {
    const byPrefix = new Map();
    for (const e of Array.isArray(statusHistory) ? statusHistory : []) {
        const c = String((e && e.comment) || '').trim();
        const m = /^\[([a-z0-9-]+)\]/i.exec(c);
        if (m) byPrefix.set(m[1].toLowerCase(), c); // later entries win — history is chronological
    }
    return [...byPrefix.values()];
};

// A record's comment with its annotations appended, as one story string. Use this wherever a comment is
// rendered for a reader; the two belong together and drift apart the moment one screen does it alone.
export const commentWithAnnotations = (record) => {
    if (!record) return '';
    return [record.comment, ...annotationLines(record.statusHistory)].filter(Boolean).join('\n');
};

// WHY A SEARCH HIT CAN BE INVISIBLE, AND WHAT TO SHOW INSTEAD (HIL-89).
//
// The reconciliation search deliberately covers `status_history.comment` as well as `comment`. It has
// to: `comment` holds only the LATEST text, and oracle-ohip's terminal stamp replaces it wholesale, so
// per-sync annotations live only in the history. Searching the comment alone once left 144 visibly
// annotated reservations unfindable.
//
// But the row renders `comment` plus only the BRACKET-PREFIXED history lines (see annotationLines), so
// an unprefixed history entry — "Missing linked Block O000002056" from an earlier attempt that has
// since been reprocessed into a different error — matches the query and appears nowhere on screen. The
// reader is left holding a result they cannot account for, which reads as a broken search.
//
// The answer is not to stop searching the history; that would take the earlier fix back out. It is to
// SHOW THE LINE THAT MATCHED, labelled as an earlier comment. Both facts are then on screen: what the
// record says now, and what it said when it matched.
//
// Matching is a case-insensitive substring rather than a reproduction of OpenSearch's phrase-prefix
// analysis. It agrees with that analyser on everything a person types into this box — whole words, and
// a trailing partial word — and being approximate here can only under-explain a hit, never invent one.
export const hiddenHistoryMatches = (record, term) => {
    const q = String(term || '').trim().toLowerCase();
    if (!q || !record) return [];
    const onScreen = commentWithAnnotations(record).toLowerCase();
    const out = [];
    const seen = new Set();
    for (const entry of (record.statusHistory || [])) {
        const line = String((entry && entry.comment) || '').trim();
        if (!line || seen.has(line)) continue;
        if (!line.toLowerCase().includes(q)) continue;
        if (onScreen.includes(line.toLowerCase())) continue;  // already rendered — do not repeat it
        seen.add(line);
        out.push(line);
    }
    return out;
};

export const prettyJson = (v) => {
    if (v == null || v === '') return '(empty)';
    if (typeof v === 'string') { try { return JSON.stringify(JSON.parse(v), null, 2); } catch (e) { return v; } }
    try { return JSON.stringify(v, null, 2); } catch (e) { return String(v); }
};

// Parse the per-call exchanges captured on an exit. The message log serialises them as a JSON string
// (ohipExchangesJson), not a raw array. Each element is { method, url, request, status, response }.
export const getEntryExchanges = (entry) => {
    if (!entry || !entry.data) return [];
    let ex = entry.data.ohipExchanges;
    if (!Array.isArray(ex) && entry.data.ohipExchangesJson) {
        try { ex = JSON.parse(entry.data.ohipExchangesJson); } catch (e) { ex = []; }
    }
    return Array.isArray(ex) ? ex : [];
};

// Pretty-print a message-log `data` object. Fields whose value is itself a JSON string — notably the
// oracle-ohip transcript keys ohipRequestJson / ohipResponseJson / ohipExchangesJson — are parsed and
// the "Json" suffix dropped, so the transcript renders as real nested JSON instead of escaped strings.
export function prettyLogData(data) {
    if (!data || typeof data !== 'object') return JSON.stringify(data, null, 2);
    const out = {};
    for (const [k, v] of Object.entries(data)) {
        if (typeof v === 'string' && (k.endsWith('Json') || /^\s*[[{]/.test(v))) {
            try { out[k.endsWith('Json') ? k.slice(0, -4) : k] = JSON.parse(v); continue; } catch (_) { /* not JSON */ }
        }
        out[k] = v;
    }
    return JSON.stringify(out, null, 2);
}

// Decode a base64 payload and pretty-print it as JSON (or XML), falling back to the raw text.
// Envelopes often carry a NESTED base64 field (e.g. a PEP message's `data` = base64 <System21> XML),
// so any base64 string value that itself decodes to XML/JSON is expanded too — the inner XML is
// pretty-printed and appended below the envelope so the whole thing is readable in one go.
export function decodePayloadB64(b64) {
    const raw = atob((b64 || '').replace(/\s/g, ''));
    try {
        return prettyDecodeObj(JSON.parse(raw));
    } catch (_) { /* not JSON — fall through to XML/raw */ }
    try {
        const doc = new DOMParser().parseFromString(raw, 'application/xml');
        if (!doc.querySelector('parsererror')) {
            return Array.from(doc.childNodes).map(c => prettyPrintXml(c, 0)).filter(s => s !== '').join('\n');
        }
    } catch (_2) {}
    return raw;
}

// atob only if the string looks like base64 that decodes to printable content; else null.
function tryDecodeB64(s) {
    if (typeof s !== 'string') return null;
    const t = s.replace(/\s/g, '');
    if (t.length < 40 || t.length % 4 !== 0 || !/^[A-Za-z0-9+/]+={0,2}$/.test(t)) return null;
    try {
        const dec = atob(t);
        if (/[\x00-\x08\x0e-\x1f]/.test(dec.slice(0, 200))) return null; // binary — not a text payload
        return dec;
    } catch (_) { return null; }
}

// Pretty-print a decoded object, expanding any nested base64 string values that themselves decode to
// XML or JSON. Nested XML is pretty-printed and collected as a trailer (so the JSON envelope stays
// clean); nested JSON is inlined as an object.
function prettyDecodeObj(obj) {
    const trailers = [];
    const walk = (o) => {
        if (Array.isArray(o)) return o.map(walk);
        if (o && typeof o === 'object') {
            const out = {};
            for (const [k, v] of Object.entries(o)) out[k] = walk(v);
            return out;
        }
        if (typeof o === 'string') {
            const dec = tryDecodeB64(o);
            if (dec != null) {
                const t = dec.trimStart();
                if (t.startsWith('<')) {
                    let xml = dec;
                    try {
                        const doc = new DOMParser().parseFromString(dec, 'application/xml');
                        if (!doc.querySelector('parsererror')) {
                            xml = Array.from(doc.childNodes).map(c => prettyPrintXml(c, 0)).filter(s => s !== '').join('\n');
                        }
                    } catch (_) {}
                    trailers.push(xml);
                    return '<decoded XML below>';
                }
                if (t.startsWith('{') || t.startsWith('[')) {
                    try { return walk(JSON.parse(dec)); } catch (_) {}
                }
            }
        }
        return o;
    };
    const top = walk(obj);
    let s = JSON.stringify(top, null, 2);
    if (trailers.length) s += '\n\n--- decoded data ---\n' + trailers.join('\n\n');
    return s;
}

// A message as a human wants to read it, whatever it happens to be.
//
// Connector payloads are XML on some legs and JSON on others, and the caller usually cannot tell which
// without looking. This sniffs the first non-space character, so one call site handles both: XML is
// re-indented through prettyPrintXml (which existed here but had no string entry point, so nothing used
// it and SOAP envelopes rendered as one unreadable wall), JSON is re-serialised with indentation, and
// anything else is returned untouched.
//
// A parse failure returns the ORIGINAL text rather than an error. A payload that is malformed is exactly
// the one somebody is trying to read, and hiding it behind "could not format" would remove the only
// evidence of what went wrong.
export function formatMessage(text) {
    if (typeof text !== 'string') return text;
    const trimmed = text.trim();
    if (!trimmed) return text;

    if (trimmed.startsWith('<')) {
        try {
            const doc = new DOMParser().parseFromString(trimmed, 'application/xml');
            if (doc.getElementsByTagName('parsererror').length > 0) return text;
            return Array.from(doc.childNodes)
                .map(n => prettyPrintXml(n, 0))
                .filter(s => s !== '')
                .join('\n');
        } catch (_) {
            return text;
        }
    }

    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try { return JSON.stringify(JSON.parse(trimmed), null, 2); } catch (_) { return text; }
    }

    return text;
}

export function prettyPrintXml(node, depth) {
    const indent = '  '.repeat(depth);
    if (node.nodeType === Node.TEXT_NODE) { const text = node.textContent.trim(); return text ? `${indent}${text}` : ''; }
    if (node.nodeType === Node.PROCESSING_INSTRUCTION_NODE) return `${indent}<?${node.nodeName} ${node.nodeValue}?>`;
    const tag = node.nodeName;
    const attrs = Array.from(node.attributes || []).map(a => ` ${a.name}="${a.value}"`).join('');
    const children = Array.from(node.childNodes).map(c => prettyPrintXml(c, depth + 1)).filter(s => s !== '');
    if (children.length === 0) return `${indent}<${tag}${attrs}/>`;
    if (children.length === 1 && !children[0].includes('\n')) return `${indent}<${tag}${attrs}>${children[0].trim()}</${tag}>`;
    return `${indent}<${tag}${attrs}>\n${children.join('\n')}\n${indent}</${tag}>`;
}
